import { useMemo } from 'react';

import {
  useSearchFilterType,
  useSearchFiltersByName,
  useSearchLocation,
  useSearchQuery,
  useSearchRadius,
} from '#/modules/search/contexts/SearchContext';
import { serializeActionDate } from '#/modules/search/helpers/actionDateSerializer';
import { radiusStringToInt } from '#/modules/search/helpers/radius';
import { searchesAreEqual } from '#/modules/search/helpers/savedSearch/searchesAreEqual';
import { filterToIndex } from '#/modules/search/helpers/savedSearches';
import type {
  ApiSavedSearchUnsaved,
  UserDashboardApiSavedSearch,
} from '#/modules/userDashboard/api/savedSearch/types/UserDashboardApiSavedSearch';
import { useSavedSearches } from '#/modules/userDashboard/contexts/SavedSearchesContext';
import { IDEALIST_TIME_ZONE } from '#/utils/constants/general/idealistTimeZone';
import { getUserTimezone } from '#/utils/timezones';

export function useCurrentSavedSearch(): {
  savedSearch: UserDashboardApiSavedSearch | null | undefined;
} {
  const { savedSearches } = useSavedSearches();

  const query = useSearchQuery();
  const searchFilterType = useSearchFilterType();
  const radius = useSearchRadius();
  const filters = useSearchFiltersByName();
  const searchLocation = useSearchLocation();

  const saveableSearchFilters = useMemo(() => {
    const timezone =
      searchLocation?.timezone ?? getUserTimezone() ?? IDEALIST_TIME_ZONE;

    if ('startsLT' in filters || 'endsGT' in filters) {
      const { _objectType, endsGT, startsLT, ...baseFilters } = filters;

      const dateFilters = serializeActionDate({
        timezone,
        endsGT: endsGT ?? null,
        startsLT: startsLT ?? null,
      });

      return {
        text: query,
        radius: radiusStringToInt(radius),
        index: filterToIndex(searchFilterType),
        location: searchLocation ?? {},
        filters: {
          ...baseFilters,
          ...dateFilters,
        },
      };
    }

    return {
      text: query,
      radius: radiusStringToInt(radius),
      index: filterToIndex(searchFilterType),
      location: searchLocation ?? {},
      filters,
    } as ApiSavedSearchUnsaved;
  }, [filters, query, radius, searchFilterType, searchLocation]);

  const savedSearch = useMemo(
    () =>
      savedSearches?.find((s) => searchesAreEqual(s, saveableSearchFilters)),
    [savedSearches, saveableSearchFilters],
  );

  return { savedSearch };
}
